/**
 * Feature flags with three layers, the same model as Fyso Business
 * (ADR 0035/0036/0084 in fyso-business): the declared default, then the
 * deploy's `FEATURE_FLAGS`, then the user's own overrides on top.
 *
 * - Defaults are OFF. A deploy that forgets a flag hides the feature instead
 *   of shipping it half-wired.
 * - A user override stores only the keys that differ from the deploy, so a
 *   later change to a deploy value still reaches users who never touched
 *   that flag. Setting a flag back to the deploy value deletes its key.
 * - Bad configuration never reads as a silent `false`: broken input keeps
 *   what the layer below decided and says so through the logger.
 * - `resolveFlags` is the single swap point: it is the only function that
 *   knows where flag values come from.
 */

export interface FlagMeta {
  /** Short name shown in the user's Experiments list. */
  label: string;
  /** One sentence about what turns on. */
  description: string;
}

/** Every flag this app knows about, with its default and how it is presented. */
export const FEATURE_FLAGS = {
  'agent-avatars': {
    default: false,
    label: 'Avatares vivos para agentes',
    description: 'Muestra a cada agente con un avatar animado que refleja lo que está haciendo.',
  },
  'agent-beta-theme': {
    default: false,
    label: 'Nuevo skin Fyso para agentes',
    description: 'Aplica el nuevo skin Fyso a toda la aplicación, con modo oscuro por defecto y selector de modo claro.',
  },
  'assistant-beta': {
    default: false,
    label: 'Assistant Beta',
    description: 'Muestra la opción Assistant al crear un agente.',
  },
  'agent-mcp-connections': {
    default: false,
    label: 'Conexiones MCP para agentes',
    description: 'Habilita conexiones MCP directas asignables a agentes (vista Beta).',
  },
} as const satisfies Record<string, FlagMeta & { default: boolean }>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;
export type FlagsSnapshot = Readonly<Record<FeatureFlagKey, boolean>>;
export type FlagOverrides = Partial<Record<FeatureFlagKey, boolean>>;

export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];

export function isFeatureFlagKey(key: unknown): key is FeatureFlagKey {
  return typeof key === 'string' && Object.hasOwn(FEATURE_FLAGS, key);
}

/** What a flag is worth when nothing else says otherwise. */
export function defaultFlags(): FlagsSnapshot {
  return Object.freeze(Object.fromEntries(FEATURE_FLAG_KEYS.map((key) => [key, FEATURE_FLAGS[key].default])) as Record<FeatureFlagKey, boolean>);
}

export interface FlagLogger {
  warn(message: string): void;
  error(message: string): void;
}

const runtimeLogger: FlagLogger = {
  warn: (message) => (globalThis as { console?: Partial<FlagLogger> }).console?.warn?.(message),
  error: (message) => (globalThis as { console?: Partial<FlagLogger> }).console?.error?.(message),
};

/** Apply declared, boolean entries; anything else is ignored and reported. */
function applyEntries(
  snapshot: Record<FeatureFlagKey, boolean>,
  entries: Record<string, unknown>,
  source: string,
  logger: FlagLogger,
): void {
  for (const [key, value] of Object.entries(entries)) {
    if (!isFeatureFlagKey(key)) {
      logger.warn(`[flags] ${source} sets unknown flag "${key}" — ignored`);
    } else if (typeof value !== 'boolean') {
      logger.warn(`[flags] ${source} value for "${key}" is not a boolean — ignored`);
    } else {
      snapshot[key] = value;
    }
  }
}

export interface ResolveFlagsOptions {
  /** Raw `FEATURE_FLAGS` value: a JSON object of flag key to boolean. */
  config?: string | null;
  /** The user's overrides (only the keys that differ from the deploy). Untrusted input. */
  overrides?: unknown;
  logger?: FlagLogger;
}

/** Resolve every flag: default → deploy config → user overrides. Never throws. */
export function resolveFlags({ config, overrides, logger = runtimeLogger }: ResolveFlagsOptions = {}): FlagsSnapshot {
  const snapshot = { ...defaultFlags() } as Record<FeatureFlagKey, boolean>;

  // Each layer degrades on its own: a broken deploy config must not cost the user their overrides.
  if (config) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(config);
    } catch (error) {
      logger.error(`[flags] FEATURE_FLAGS is not valid JSON (${error instanceof Error ? error.message : String(error)}) — using defaults`);
    }
    if (parsed !== undefined) {
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        logger.error('[flags] FEATURE_FLAGS must be a JSON object of flag to boolean — using defaults');
      } else {
        applyEntries(snapshot, parsed as Record<string, unknown>, 'FEATURE_FLAGS', logger);
      }
    }
  }

  if (overrides !== undefined && overrides !== null) {
    if (typeof overrides !== 'object' || Array.isArray(overrides)) {
      logger.error('[flags] user overrides must be an object of flag to boolean — ignored');
    } else {
      applyEntries(snapshot, overrides as Record<string, unknown>, 'user overrides', logger);
    }
  }

  return Object.freeze(snapshot);
}

/** The overrides to store after the user sets `key` to `value`: a diff against the deploy, never a snapshot. */
export function nextOverrides(
  current: unknown,
  key: FeatureFlagKey,
  value: boolean,
  deployValue: boolean,
  logger: FlagLogger = runtimeLogger,
): FlagOverrides {
  // Re-validate what is stored: a corrupt column degrades to "no overrides", never to a crash.
  const clean: FlagOverrides = {};
  if (current && typeof current === 'object' && !Array.isArray(current)) {
    for (const [k, v] of Object.entries(current)) {
      if (isFeatureFlagKey(k) && typeof v === 'boolean') clean[k] = v;
      else logger.warn(`[flags] dropping invalid stored override "${k}"`);
    }
  }
  if (value === deployValue) delete clean[key];
  else clean[key] = value;
  return clean;
}

/** One row of the user's Experiments list. */
export interface FlagStatus extends FlagMeta {
  key: FeatureFlagKey;
  /** What applies to this user right now. */
  value: boolean;
  /** What applies with no user override. */
  deployValue: boolean;
  /** True when the user's choice differs from the deploy. */
  overridden: boolean;
}

/** Every declared flag with its effective and deploy values, for the Experiments screen. */
export function describeFlags(options: ResolveFlagsOptions = {}): FlagStatus[] {
  const effective = resolveFlags(options);
  const deploy = resolveFlags({ config: options.config, logger: { warn: () => {}, error: () => {} } });
  return FEATURE_FLAG_KEYS.map((key) => ({
    key,
    label: FEATURE_FLAGS[key].label,
    description: FEATURE_FLAGS[key].description,
    value: effective[key],
    deployValue: deploy[key],
    overridden: effective[key] !== deploy[key],
  }));
}
